import {
    composeBlockers,
    ManualBlocker,
    MIN_SCREEN_HEIGHT,
    NativeFlagBlocker,
    OtherSurfaceVisibleBlocker,
    PlayerFullscreenBlocker,
    ScreenHeightBlocker,
    VisibleElementBlocker,
} from './blockers';
import { BrandAlertsClient } from './client';
import { IosHost, VideoAssistantHost, VideoPageHost } from './hosts';
import { IosSignals, WebSignals } from './signals';
import { HttpTransport, iosAuthHeaders, webAuthHeaders } from './transport';
import {
    ChatbotActivitySource,
    ChatbotInactivityTrigger,
    NativeActivitySource,
    NativePlaybackTrigger,
    PlayerPlaybackTrigger,
} from './triggers';
import type { BrandAlertsConfig, BrandAlertsPlayer, BrandAlertSurface } from './types';
import { SURFACE } from './types';

export { BrandAlertsClient } from './client';
export { VideoAssistantHost } from './hosts';
export { BrandAlertPopup } from './popup';
export { DISMISS_TYPE, SURFACE } from './types';
export type { BrandAlertCreativeData, BrandAlertsConfig, BrandAlertsPlayer, DismissType } from './types';

const INTERSTITIAL_SELECTOR = '#subscriptions-form-modal.show';

/**
 * The Video Assistant's mobile panel, which is `position: fixed; inset: 0` at a
 * z-index above the video-player-page sideguide (`scss/components/chatbot/inline.scss`).
 */
const MOBILE_CHATBOT_PANEL_SELECTOR = '.vm-chatbot-video-assistant--mobile-in';

/**
 * Returns null on pages without the sideguide shell (e.g. channel pages),
 * matching the old constructor's silent bail-out.
 */
const findPopupFrame = (surface: BrandAlertSurface): HTMLElement | null =>
    document.querySelector<HTMLElement>(`.js_popupFrame[data-surface="${surface}"]`);

/**
 * The video player page on web.
 *
 * The page keeps `setBlocked()` and `dismiss()` as before; the difference is
 * that both now route through collaborators rather than subclass overrides.
 */
export const createVideoPageBrandAlerts = (
    config: BrandAlertsConfig,
    player: BrandAlertsPlayer,
): BrandAlertsClient | null => {
    const popupFrame = findPopupFrame(SURFACE.VIDEO_PLAYER_PAGE);
    if (!popupFrame) return null;

    const manualBlocker = new ManualBlocker();

    return new BrandAlertsClient({
        surface: SURFACE.VIDEO_PLAYER_PAGE,
        popupFrame,
        transport: new HttpTransport(config, webAuthHeaders, SURFACE.VIDEO_PLAYER_PAGE),
        host: new VideoPageHost(popupFrame),
        trigger: new PlayerPlaybackTrigger(player),
        signals: new WebSignals(player),
        manualBlocker,
        blockers: composeBlockers([
            manualBlocker,
            new OtherSurfaceVisibleBlocker(SURFACE.VIDEO_PLAYER_PAGE),
            new PlayerFullscreenBlocker(player),
            new ScreenHeightBlocker(MIN_SCREEN_HEIGHT),
            new VisibleElementBlocker(INTERSTITIAL_SELECTOR),
            new VisibleElementBlocker(MOBILE_CHATBOT_PANEL_SELECTOR),
        ]),
    });
};

/**
 * The Video Assistant surface on web (BVA).
 *
 * The same client with four different collaborators. Returns `null` when the
 * assistant shell is absent, so a page rendering only the video-player-page
 * include is unaffected.
 */
export const createBvaBrandAlerts = (
    config: BrandAlertsConfig,
    player: BrandAlertsPlayer,
): BrandAlertsClient | null => {
    const popupFrame = findPopupFrame(SURFACE.BVA);
    if (!popupFrame) return null;

    const manualBlocker = new ManualBlocker();
    // Idle until the HCP asks the assistant something; never fetches otherwise.
    const trigger = new ChatbotInactivityTrigger(new ChatbotActivitySource());

    return new BrandAlertsClient({
        surface: SURFACE.BVA,
        popupFrame,
        transport: new HttpTransport(config, webAuthHeaders, SURFACE.BVA, () => trigger.chatViewedUuid),
        host: new VideoAssistantHost(popupFrame),
        trigger,
        signals: new WebSignals(player),
        manualBlocker,
        blockers: composeBlockers([
            manualBlocker,
            new OtherSurfaceVisibleBlocker(SURFACE.BVA),
            new PlayerFullscreenBlocker(player),
            new ScreenHeightBlocker(MIN_SCREEN_HEIGHT),
            new VisibleElementBlocker(INTERSTITIAL_SELECTOR),
        ]),
    });
};

/**
 * The iOS webview page served by `BrandAlertClientView`.
 */
export const createIosBrandAlerts = (config: BrandAlertsConfig): BrandAlertsClient | null => {
    const popupFrame = findPopupFrame(SURFACE.VIDEO_PLAYER_PAGE);
    if (!popupFrame) return null;

    return new BrandAlertsClient({
        surface: SURFACE.VIDEO_PLAYER_PAGE,
        popupFrame,
        transport: new HttpTransport(config, iosAuthHeaders, SURFACE.VIDEO_PLAYER_PAGE),
        host: new IosHost(popupFrame),
        trigger: new NativePlaybackTrigger(),
        signals: new IosSignals(),
        blockers: composeBlockers([
            // iOS blocks only on native comment visibility; the rest retry, matching
            // the flags the old subclass kept.
            new NativeFlagBlocker(
                'block',
                { on: 'ios_comments_visible', off: 'ios_comments_invisible' },
                'comments_prevent_showing',
            ),
            new NativeFlagBlocker('postpone', { on: 'ios_fullscreen_enter', off: 'ios_fullscreen_exit' }),
            new NativeFlagBlocker('postpone', { on: 'ios_screen_too_small' }),
            new NativeFlagBlocker('postpone', {
                on: 'ios_interstitial_visible',
                off: 'ios_interstitial_invisible',
            }),
        ]),
    });
};

/**
 * The iOS BVA webview, loaded by the app with `surface=bva` after the HCP's first
 * prompt to the native Video Assistant.
 */
export const createIosBvaBrandAlerts = (config: BrandAlertsConfig): BrandAlertsClient | null => {
    const popupFrame = findPopupFrame(SURFACE.BVA);
    if (!popupFrame) return null;

    const trigger = new ChatbotInactivityTrigger(new NativeActivitySource());

    return new BrandAlertsClient({
        surface: SURFACE.BVA,
        popupFrame,
        transport: new HttpTransport(config, iosAuthHeaders, SURFACE.BVA, () => trigger.chatViewedUuid),
        host: new IosHost(popupFrame),
        trigger,
        signals: new IosSignals(),
        blockers: composeBlockers([
            new NativeFlagBlocker('postpone', { on: 'ios_fullscreen_enter', off: 'ios_fullscreen_exit' }),
        ]),
    });
};
